import {
  Box,
  Center,
  HStack,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {
  CaretRight,
  Wrinkles,
  WrinklesHide,
} from '@time-line/components/icons';
import { useSanityProduct } from 'hooks/product/useSanityProduct';
import { PROMOTIONS } from 'lib/constants/routes';
import { useImage } from 'lib/sanity';
import NextImage from 'next/image';
import { useRouter } from 'next/router';
import type { TouchEvent } from 'react';
import { useRef, useState } from 'react';
import type { BeforeAfterResult as BeforeAfterSliderProps } from 'types/cms/blocks';
import { isBrowser } from 'utils/environment';

import { SLIDER_CONTAINER_BREAKPOINT } from './constants';
import { ProductLink } from './ProductLink';
import { SliderItem } from './SliderItem';

export function Slider({
  before,
  after,
  product,
  showProduct = true,
}: BeforeAfterSliderProps) {
  const sanityProduct = useSanityProduct(product?.slug);
  const currentPath = useRouter().asPath.split('?')[0];
  const isPromotionPage = currentPath.includes(PROMOTIONS);
  const afterBlurImageProps = useImage(after.image, { fill: true, ratio: 1 });
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [showOverlays, setShowOverlays] = useState(true);
  const [imageRevealFraction, setImageRevealFraction] = useState(0.5);
  const imageRevealPercentage = `${imageRevealFraction * 100}%`;

  const slide = (xPos: number): void => {
    const containerBoundingRect =
      imageContainerRef.current?.getBoundingClientRect();

    if (!containerBoundingRect) return;

    setImageRevealFraction(() => {
      if (xPos < containerBoundingRect.left) return 0;
      if (xPos > containerBoundingRect.right) return 1;

      return (xPos - containerBoundingRect.left) / containerBoundingRect.width;
    });
  };

  const handleMouseUp = (): void => {
    if (!isBrowser) return;

    window.onmousemove = null;
    window.onmouseup = null;
  };

  const handleMouseMove = (e: globalThis.MouseEvent): void => {
    slide(e.clientX);
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>): void => {
    slide(e.touches[0].clientX);
  };

  const handleMouseDown = (): void => {
    if (!isBrowser) return;

    window.onmousemove = handleMouseMove;
    window.onmouseup = handleMouseUp;
  };

  return (
    <Box
      position="relative"
      borderRadius="base"
      overflow="hidden"
      sx={{ containerType: 'inline-size' }}
    >
      <Box
        aria-hidden
        position="absolute"
        inset={0}
        zIndex={0}
        overflow="hidden"
        pointerEvents="none"
      >
        {afterBlurImageProps && (
          <Box position="absolute" top="-15%" left="-15%" w="130%" h="130%">
            <NextImage
              {...afterBlurImageProps}
              sizes="(min-width: 960px) 33vw, 90vw"
              style={{ objectFit: 'cover' }}
            />
          </Box>
        )}
      </Box>

      <VStack spacing={0} align="stretch" position="relative" zIndex={1}>
        <Box
          ref={imageContainerRef}
          pos="relative"
          borderTopRadius="base"
          overflow="hidden"
          userSelect="none"
        >
          <SliderItem
            {...after}
            className="before-after_after"
            isAfter
            showOverlay={showOverlays}
          />
          <SliderItem
            {...before}
            className="before-after_before"
            pos="absolute"
            w="full"
            h="full"
            top={0}
            left={0}
            clipPath={`polygon(0 0, ${imageRevealPercentage} 0, ${imageRevealPercentage} 100%, 0 100%)`}
            showOverlay={showOverlays}
          />
          <Box
            pos="absolute"
            top={0}
            left={imageRevealPercentage}
            h="full"
            w="1px"
            bg="gray.50"
          />
          <Center
            role="group"
            cursor="grab"
            sx={{ touchAction: 'none' }}
            onMouseDown={handleMouseDown}
            onTouchMove={handleTouchMove}
            pos="absolute"
            zIndex={3}
            bg="gray.50"
            borderRadius="base"
            borderColor="gray.600"
            boxSize="44px"
            ml="-22px"
            mt="-22px"
            top="50%"
            left={imageRevealPercentage}
            boxShadow="0 0 0 1px rgba(0, 0, 0, 0.08), 0px 0px 0px 1px rgba(0, 0, 0, 0.02), 0px 1px 4px 0px rgba(0, 0, 0, 0.06), 0px 6px 24px 0px rgba(0, 0, 0, 0.06)"
          >
            <HStack w="full" spacing={0} justifyContent="center">
              <CaretRight
                boxSize="xs"
                mr="-3px"
                sx={{
                  transform: 'rotate(180deg)',
                }}
              />
              <CaretRight ml="-3px" boxSize="xs" />
            </HStack>
          </Center>
          {before.imageOverlay && (
            <Tooltip
              label={
                <Text size="detail" fontWeight="medium" color="gray.50">
                  {showOverlays ? 'Hide wrinkle lines' : 'Show wrinkle lines'}
                </Text>
              }
              maxWidth="100%"
              bg="blackAlpha.500"
              borderRadius="base"
              px="4xs"
              py="5xs"
              backdropFilter="blur(10px)"
              aria-label={
                showOverlays ? 'Hide wrinkle lines' : 'Show wrinkle lines'
              }
            >
              <IconButton
                className="before-after_toggle-wrinkles"
                pos="absolute"
                bottom="2xs"
                right="2xs"
                icon={
                  showOverlays ? (
                    <Wrinkles boxSize="xs" color="gray.50" />
                  ) : (
                    <WrinklesHide boxSize="xs" color="gray.50" />
                  )
                }
                variant="unstyled"
                bg="blackAlpha.400"
                backdropFilter="blur(50px)"
                boxSize="32px"
                borderRadius="base"
                aria-label={
                  showOverlays ? 'Hide wrinkle lines' : 'Show wrinkle lines'
                }
                onClick={() => setShowOverlays(!showOverlays)}
                sx={{
                  [`@container (min-width: ${SLIDER_CONTAINER_BREAKPOINT})`]: {
                    bottom: 'sm',
                    right: 'sm',
                  },
                }}
              />
            </Tooltip>
          )}
        </Box>
        {sanityProduct && showProduct && (
          <ProductLink
            sanityProduct={sanityProduct}
            isPromotionPage={isPromotionPage}
          />
        )}
      </VStack>
    </Box>
  );
}
